import React from 'react';
import { FaUpload, FaFileAlt, FaSpinner, FaTimes, FaCheck } from 'react-icons/fa';
import PropTypes from 'prop-types';

const KYCForm = ({ formData, handleChange, handleCheckboxChange, uploadStatus = {} }) => {
  // Initialize default formData structure if needed
  const safeFormData = {
    proofOfAddress: '',
    proofOfAddressFile: null,
    governmentIdType: '',
    governmentIdFile: null,
    idNumber: '',
    financialStatementsFile: null,
    socialSecurityNumber: '',
    sourceOfFunds: '',
    otherSource: '',
    estimatedNetWorth: '',
    fullName: '',
    dateOfBirth: '',
    signatureFile: null,
    residentialAddress: '',
    city: '',
    state: '',
    postalCode: '',
    ...formData, // Override with passed props
  };

  const documentTypes = [
    { value: '', label: 'Select document type' },
    { value: 'Utility bill', label: 'Utility bill' },
    { value: 'Bank statement', label: 'Bank statement' },
    { value: 'Lease Agreement', label: 'Lease Agreement' },
  ];

  const idTypes = [
    { value: '', label: 'Select ID type' },
    { value: "Driver's license", label: "Driver's license" },
    { value: 'Passport', label: 'Passport' },
    { value: 'National ID', label: 'National ID' },
  ];

  const netWorthOptions = [
    { value: '<$250k', label: '<$250k' },
    { value: '$250k–$1M', label: '$250k–$1M' },
    { value: '$1M+', label: '$1M+' },
  ];

  const sourceOfFundsOptions = [
    { value: 'Employment', label: 'Employment' },
    { value: 'Investment Income', label: 'Investment Income' },
    { value: 'Business Revenue', label: 'Business Revenue' },
    { value: 'Other', label: 'Other' },
  ];

  const handleFileRemove = (fieldName) => {
    const event = {
      target: {
        name: fieldName,
        value: null,
        files: null,
      },
    };
    handleChange(event);
  };

  const FileUploadField = ({ name, label, value, onChange, required = true }) => {
  const file = value;
  const fileName = file?.name || '';
  const uploadStatusForField = uploadStatus[name] || {};

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">{label} {required && <span className="text-red-500">*</span>}</label>
      <label className="flex flex-col items-center px-4 py-6 bg-white rounded-lg border-2 border-dashed border-gray-300 cursor-pointer hover:border-gray-500 transition-colors duration-200">
        <FaUpload className="text-gray-500 text-xl mb-2" />
        <span className="text-sm text-gray-600 text-center">
          {fileName || `Click to upload or drag and drop (Max size: 5MB)`}
        </span>
        <input
          type="file"
          name={name}
          onChange={(e) => {
            // Make sure we're passing the file object correctly
            const file = e.target.files[0];
            onChange({
              target: {
                name: name,
                value: file,
                files: e.target.files
              }
            });
          }}
          className="hidden"
          accept=".pdf,.jpg,.jpeg,.png"
          required={required && !fileName} // Only require if no file is selected
        />
      </label>
      
      {fileName && (
        <div className="mt-2 flex justify-between items-center bg-gray-50 p-3 rounded-lg border border-gray-200">
          <div className="flex items-center">
            <FaFileAlt className="text-gray-500 mr-2" />
            <span className="text-sm font-medium">{fileName}</span>
          </div>
          <button
            type="button"
            onClick={() => {
              // Reset the file input when removing
              const event = {
                target: {
                  name: name,
                  value: null,
                  files: null
                }
              };
              onChange(event);
            }}
            className="text-gray-400 hover:text-red-500 transition-colors"
            aria-label="Remove file"
          >
            <FaTimes />
          </button>
        </div>
      )}
      
      {uploadStatusForField.status && (
        <div className="mt-2 text-sm">
          {uploadStatusForField.status === 'uploading' && (
            <div className="flex items-center text-gray-300">
              <FaSpinner className="animate-spin mr-2" />
              Uploading... {uploadStatusForField.progress || 0}%
            </div>
          )}
          {uploadStatusForField.status === 'completed' && (
            <div className="flex items-center text-green-600">
              <FaCheck className="mr-2" />
              Upload complete!
            </div>
          )}
          {uploadStatusForField.status === 'error' && (
            <div className="flex items-center text-red-600">
              <FaTimes className="mr-2" />
              Upload failed. Please try again.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

  return (
    <section className="border-b border-gray-100 pb-8 md:pb-12">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-xl md:text-2xl font-bold mb-6 md:mb-8 text-gray-900 tracking-tight">
          <span className="block text-sm font-normal text-gray-500 mt-1">Complete your KYC to access all features</span>
        </h2>
        
        <div className="space-y-6 md:space-y-8">
          {/* Progress indicator */}
          <div className="bg-gray-50 p-4 rounded-xl border border-gray-100 flex items-start">
            <div className="bg-gray-200 text-gray-600 p-2 rounded-lg mr-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9z" clipRule="evenodd" />
              </svg>
            </div>
            <div>
              <h3 className="font-medium text-gray-900">Step 1 of 3: KYC</h3>
              <p className="text-sm text-gray-600 mt-1">Please provide the required documents to complete your verification.</p>
            </div>
          </div>

          {/* Proof of Address */}
          <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
            <div className="flex items-center mb-4">
              <div className="bg-gray-200 text-gray-600 p-1.5 rounded-lg mr-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-[#000]">Proof of Address</h3>
            </div>
            
            <div className="space-y-4">
              <div className="bg-gray-50/50 p-4 rounded-lg border border-gray-100">
                <div className="flex">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-300 mr-2 mt-0.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm text-gray-700">
                    <span className="font-medium">Accepted documents:</span> Utility bill, Bank statement, Lease agreement<br />
                    <span className="font-medium">Requirements:</span> Must show your name and address clearly, issued within last 7 months
                  </p>
                </div>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Document Type <span className="text-red-500">*</span></label>
                  <select
                    name="proofOfAddress"
                    value={safeFormData.proofOfAddress}
                    onChange={handleChange}
                    className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500 bg-white transition-all duration-150"
                    required
                  >
                    {documentTypes.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                
                <FileUploadField 
                  name="proofOfAddressFile"
                  label="Upload Proof of Address"
                  onChange={handleChange}
                  value={safeFormData.proofOfAddressFile}
                  required
                />
              </div>
            </div>
          </div>

          {/* Government ID Section */}
          <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
            <div className="flex items-center mb-4">
              <div className="bg-gray-100 text-gray-800 p-1.5 rounded-lg mr-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 2a1 1 0 00-1 1v1a1 1 0 002 0V3a1 1 0 00-1-1zM4 4h3a3 3 0 006 0h3a2 2 0 012 2v9a2 2 0 01-2 2H4a2 2 0 01-2-2V6a2 2 0 012-2zm2.5 7a1.5 1.5 0 100-3 1.5 1.5 0 000 3zm2.45 4a2.5 2.5 0 10-4.9 0h4.9zM12 9a1 1 0 100 2h3a1 1 0 100-2h-3zm-1 4a1 1 0 011-1h2a1 1 0 110 2h-2a1 1 0 01-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Government Identification</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Government ID Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">ID Type <span className="text-red-500">*</span></label>
                <select
                  name="governmentIdType"
                  value={safeFormData.governmentIdType}
                  onChange={handleChange}
                  className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500 bg-white"
                  required
                >
                  {idTypes.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                
                <FileUploadField 
                  name="governmentIdFile"
                  label="Upload ID Document"
                  onChange={handleChange}
                  value={safeFormData.governmentIdFile}
                  required
                />
              </div>
              
              {/* ID Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">ID Number <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  name="idNumber"
                  value={safeFormData.idNumber}
                  onChange={handleChange}
                  className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500 bg-white"
                  placeholder="Enter your ID number"
                  required
                />
              </div>
            </div>
          </div>

          {/* Personal Information */}
          <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
            <div className="flex items-center mb-4">
              <div className="bg-gray-100 text-gray-800 p-1.5 rounded-lg mr-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Personal Information</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Full Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Legal Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  name="fullName"
                  value={safeFormData.fullName}
                  onChange={handleChange}
                  className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500 bg-white"
                  placeholder="As shown on your ID"
                  required
                />
              </div>
              
              {/* Date of Birth */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth <span className="text-red-500">*</span></label>
                <div className="relative">
                  <input
                    type="date"
                    name="dateOfBirth"
                    value={safeFormData.dateOfBirth}
                    onChange={handleChange}
                    className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500 bg-white appearance-none"
                    required
                  />
                </div>
              </div>
              
              {/* SSN */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Social Security Number <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  name="socialSecurityNumber"
                  value={safeFormData.socialSecurityNumber}
                  onChange={handleChange}
                  className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500 bg-white"
                  placeholder="XXX-XX-XXXX"
                  required
                />
              </div>
            </div>
          </div>

          {/* Address Information */}
          <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
            <div className="flex items-center mb-4">
              <div className="bg-gray-100 text-gray-800 p-1.5 rounded-lg mr-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Residential Address</h3>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Street Address <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  name="residentialAddress"
                  value={safeFormData.residentialAddress}
                  onChange={handleChange}
                  className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500 bg-white"
                  placeholder="123 Main St"
                  required
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* City */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">City <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    name="city"
                    value={safeFormData.city}
                    onChange={handleChange}
                    className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500 bg-white"
                    required
                  />
                </div>
                
                {/* State */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">State/Province <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    name="state"
                    value={safeFormData.state}
                    onChange={handleChange}
                    className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500 bg-white"
                    required
                  />
                </div>
                
                {/* Postal Code */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Postal Code <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    name="postalCode"
                    value={safeFormData.postalCode}
                    onChange={handleChange}
                    className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500 bg-white"
                    required
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Financial Information */}
          <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
            <div className="flex items-center mb-4">
              <div className="bg-gray-100 text-gray-800 p-1.5 rounded-lg mr-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Financial Information</h3>
            </div>
            
            <div className="space-y-6">
              {/* Financial Statements */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Financial Statements <span className="text-red-500">*</span></label>
                <div className="bg-gray-50/50 p-4 rounded-lg border border-gray-100 mb-4">
                  <div className="flex">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-300 mr-2 mt-0.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9z" clipRule="evenodd" />
                    </svg>
                    <p className="text-sm text-gray-700">
                      <span className="font-medium">Accepted documents:</span> Bank statements, Investment account statements<br />
                      <span className="font-medium">Requirements:</span> Last 7 months, must clearly show balances and account ownership
                    </p>
                  </div>
                </div>
                <FileUploadField 
                  name="financialStatementsFile"
                  label="Upload Financial Statements"
                  onChange={handleChange}
                  value={safeFormData.financialStatementsFile}
                  required
                />
              </div>
              
              {/* Source of Funds */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Source of Funds <span className="text-red-500">*</span></label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {sourceOfFundsOptions.map((option) => (
                    <div key={option.value} className="relative flex items-start">
                      <div className="flex items-center h-5">
                        <input
                          type="checkbox"
                          name="sourceOfFunds"
                          value={option.value}
                          checked={safeFormData.sourceOfFunds === option.value}
                          onChange={handleCheckboxChange}
                          className="focus:ring-gray-500 h-4 w-4 text-gray-300 border-gray-300 rounded"
                        />
                      </div>
                      <div className="ml-3 text-sm">
                        <label className="font-medium text-gray-700">{option.label}</label>
                        {option.value === 'Other' && safeFormData.sourceOfFunds === 'Other' && (
                          <input
                            type="text"
                            name="otherSource"
                            value={safeFormData.otherSource}
                            onChange={handleChange}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500 sm:text-sm"
                            placeholder="Please specify"
                          />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Estimated Net Worth */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Estimated Net Worth <span className="text-red-500">*</span></label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {netWorthOptions.map((option) => (
                    <div key={option.value} className="relative flex items-start">
                      <div className="flex items-center h-5">
                        <input
                          type="radio"
                          name="estimatedNetWorth"
                          value={option.value}
                          checked={safeFormData.estimatedNetWorth === option.value}
                          onChange={handleChange}
                          className="focus:ring-gray-500 h-4 w-4 text-gray-300 border-gray-300"
                          required
                        />
                      </div>
                      <div className="ml-3 text-sm">
                        <label className="font-medium text-gray-700">{option.label}</label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Signature */}
          <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
            <div className="flex items-center mb-4">
              <div className="bg-gray-100 text-gray-800 p-1.5 rounded-lg mr-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Signature</h3>
            </div>
            
            <div className="bg-gray-50/50 p-4 rounded-lg border border-gray-100 mb-4">
              <div className="flex">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-300 mr-2 mt-0.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9z" clipRule="evenodd" />
                </svg>
                <p className="text-sm text-gray-700">
                  <span className="font-medium">Instructions:</span> Sign a blank white paper with black ink, then upload a clear photo or scan.<br />
                  Your signature must match the one on your government ID and be fully visible.
                </p>
              </div>
            </div>
            
            <FileUploadField 
              name="signatureFile"
              label="Upload Signature"
              onChange={handleChange}
              value={safeFormData.signatureFile}
              required
            />
          </div>
        </div>
      </div>
    </section>
  );
};

KYCForm.propTypes = {
  formData: PropTypes.shape({
    proofOfAddress: PropTypes.string,
    proofOfAddressFile: PropTypes.instanceOf(File),
    governmentIdType: PropTypes.string,
    governmentIdFile: PropTypes.instanceOf(File),
    idNumber: PropTypes.string,
    financialStatementsFile: PropTypes.instanceOf(File),
    socialSecurityNumber: PropTypes.string,
    sourceOfFunds: PropTypes.string,
    otherSource: PropTypes.string,
    estimatedNetWorth: PropTypes.string,
    fullName: PropTypes.string,
    dateOfBirth: PropTypes.string,
    signatureFile: PropTypes.instanceOf(File),
    residentialAddress: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    postalCode: PropTypes.string,
  }).isRequired,
  handleChange: PropTypes.func.isRequired,
  handleCheckboxChange: PropTypes.func.isRequired,
  uploadStatus: PropTypes.object,
};

KYCForm.defaultProps = {
  uploadStatus: {},
};

export default KYCForm;